import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { SendHorizonal, Sparkles } from "lucide-react";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useBrandContext } from "@/contexts/brand-context";
import { useAssistantStore } from "@/stores/assistant-store";
import { useGlobalHotkey } from "@/hooks/use-global-hotkey";
import {
  useAssistantChat,
  useAssistantThreads,
  useCreateThread,
  useRespondConfirmation,
  useThreadMessages,
} from "@/hooks/use-assistant";
import { MessageList } from "./message-list";
import { ThreadSwitcher } from "./thread-switcher";

/**
 * "Pilot" — the brand-scoped AI assistant slide-over (spec §1 item 1).
 * Mounted once per brand subtree in BrandLayout; toggled by ⌘J everywhere
 * (including inside inputs). Threads are lazily created on first send to
 * avoid empty-thread spam.
 */
export function AssistantPanel() {
  const { t } = useTranslation("assistant");
  const { brand } = useBrandContext();
  const brandId = brand.id;
  const brandSlug = brand.slug;

  const open = useAssistantStore((s) => s.open);
  const activeThreadId = useAssistantStore((s) => s.activeThreadId);
  const prefill = useAssistantStore((s) => s.prefill);
  const openPanel = useAssistantStore((s) => s.openPanel);
  const closePanel = useAssistantStore((s) => s.closePanel);
  const togglePanel = useAssistantStore((s) => s.togglePanel);
  const setActiveThread = useAssistantStore((s) => s.setActiveThread);
  const consumePrefill = useAssistantStore((s) => s.consumePrefill);
  const syncBrand = useAssistantStore((s) => s.syncBrand);

  useGlobalHotkey("mod+j", togglePanel);

  const threadsQuery = useAssistantThreads(open ? brandId : undefined);
  const messagesQuery = useThreadMessages(open ? activeThreadId : null);
  const createThread = useCreateThread(brandId);
  const respond = useRespondConfirmation(activeThreadId, brandId);
  const chat = useAssistantChat(brandId, activeThreadId);
  const stopChat = chat.stop;

  const [draft, setDraft] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  // Brand switch: the panel survives :brandSlug changes (and this store
  // survives remounts), so brand A's thread/prefill/draft must never carry
  // into brand B — a send would target the old brand's thread, with tools
  // executing against it. syncBrand clears activeThreadId + prefill in the
  // store; here we also abort any in-flight stream and drop the local tail.
  useEffect(() => {
    if (syncBrand(brandId)) {
      stopChat();
      setDraft("");
    }
  }, [brandId, syncBrand, stopChat]);

  // Consume any palette prefill whenever one appears while the panel is open
  // (subscribes to the value — "Ask Pilot" can fire with the panel already
  // open, not just on the closed → open transition).
  useEffect(() => {
    if (!open || prefill === null) return;
    const value = consumePrefill();
    if (value) {
      setDraft(value);
      requestAnimationFrame(() => textareaRef.current?.focus());
    }
  }, [open, prefill, consumePrefill]);

  const latestThreadId = threadsQuery.data?.[0]?.id ?? null;
  useEffect(() => {
    if (open && !activeThreadId && latestThreadId) {
      setActiveThread(latestThreadId);
    }
  }, [open, activeThreadId, latestThreadId, setActiveThread]);

  const disabledState =
    (threadsQuery.error as { code?: string } | null)?.code === "ASSISTANT_DISABLED";

  const handleSend = async () => {
    const content = draft.trim();
    if (!content || chat.status === "streaming" || createThread.isPending) return;
    setDraft("");

    let threadId = activeThreadId;
    if (!threadId) {
      try {
        const thread = await createThread.mutateAsync();
        threadId = thread.id;
        setActiveThread(threadId);
      } catch {
        setDraft(content); // toast fired by the hook; keep the user's text
        return;
      }
    }
    void chat.send(content, threadId);
  };

  const handleNewThread = () => {
    // Lazy: clear the active thread; the next send creates the real thread.
    setActiveThread(null);
    textareaRef.current?.focus();
  };

  return (
    <Sheet open={open} onOpenChange={(v) => (v ? openPanel() : closePanel())}>
      <SheetContent
        side="right"
        className="sm:max-w-md w-full p-0 flex flex-col gap-0"
        aria-describedby={undefined}
      >
        <SheetHeader className="flex-shrink-0 border-b border-border px-4 py-3 space-y-0">
          <div className="flex items-center justify-between pr-8">
            <SheetTitle className="flex items-center gap-1.5 text-sm font-semibold">
              <Sparkles size={14} className="text-primary" />
              {t("panel.title")}
            </SheetTitle>
            <SheetDescription className="sr-only">
              {t("panel.description")}
            </SheetDescription>
            {!disabledState && (
              <ThreadSwitcher
                threads={threadsQuery.data}
                activeThreadId={activeThreadId}
                onSelect={(id) => setActiveThread(id)}
                onNewThread={handleNewThread}
                creating={createThread.isPending}
              />
            )}
          </div>
        </SheetHeader>

        {disabledState ? (
          <div className="flex-1 flex items-center justify-center px-6">
            <div className="text-center space-y-1">
              <p className="text-sm font-medium">{t("panel.disabled.heading")}</p>
              <p className="text-[12px] text-muted-foreground">
                {t("panel.disabled.body")}
              </p>
            </div>
          </div>
        ) : (
          <>
            <MessageList
              history={messagesQuery.data}
              historyLoading={!!activeThreadId && messagesQuery.isLoading}
              optimisticUserMessage={chat.optimisticUserMessage}
              streamText={chat.streamText}
              toolEvents={chat.toolEvents}
              pendingConfirms={chat.pendingConfirms}
              status={chat.status}
              error={chat.error}
              onRetry={chat.retry}
              respond={respond}
              brandSlug={brandSlug}
            />

            <div className="flex-shrink-0 border-t border-border p-3">
              <div className="flex items-end gap-2">
                <textarea
                  ref={textareaRef}
                  value={draft}
                  onChange={(e) => setDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void handleSend();
                    }
                  }}
                  placeholder={t("panel.placeholder")}
                  rows={2}
                  className="flex-1 resize-none rounded-md border border-input bg-background px-3 py-2 text-[13px] placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                />
                <Button
                  size="icon"
                  className="h-9 w-9 flex-shrink-0"
                  aria-label={t("panel.send")}
                  disabled={
                    !draft.trim() || chat.status === "streaming" || createThread.isPending
                  }
                  onClick={() => void handleSend()}
                >
                  <SendHorizonal size={15} />
                </Button>
              </div>
            </div>
          </>
        )}
      </SheetContent>
    </Sheet>
  );
}
